import { Injectable, Injector, inject, runInInjectionContext } from '@angular/core';
import { IProductDataV3, Product } from '@df/catalog/product/product';
import { NProductConfiguration } from '@df/module-product-configuration/df-module-product-configuration.interfaces';
import { Nexus } from '@tomandco/nexus-api-types';
import { GROUPED_ITEM_DATA_TOKEN, GroupedItem } from './grouped-item';

@Injectable()
export class ProductConfigurationGroupedItemsService {
    static readonly itemInstance = GroupedItem;
    private readonly injector = inject(Injector);

    getItems(product: Product<IProductDataV3>, config?: NProductConfiguration.Grouped.Config): GroupedItem[] {
        const rawGrouped = product.data?.options?.grouped;
        const items = Array.isArray(rawGrouped) ? rawGrouped : [];
        const ItemClass = (this.constructor as typeof ProductConfigurationGroupedItemsService).itemInstance;
        let groupedItems = items.map(data => {
            const itemInjector = Injector.create({
                providers: [
                    {
                        provide: GROUPED_ITEM_DATA_TOKEN,
                        useValue: data
                    },
                    {
                        provide: GroupedItem,
                        useClass: ItemClass
                    }
                ],
                parent: this.injector
            });
            return runInInjectionContext(itemInjector, () => inject(GroupedItem));
        });

        if (config?.showOutOfStock === false) {
            groupedItems = groupedItems.filter(item => item.isOutOfStock);
        }

        if (config?.sortBy) {
            groupedItems = this.sortItems(groupedItems, config.sortBy);
        }

        return groupedItems;
    }

    private sortItems(items: GroupedItem[], sortBy: 'position' | 'name' | 'price'): GroupedItem[] {
        if (!Array.isArray(items) || items.length === 0) {
            return [];
        }
        const itemsCopy = [...items];
        switch (sortBy) {
            case 'name':
                return itemsCopy.sort((a, b) => (a.product.name || '').localeCompare(b.product.name || ''));
            case 'price':
                return itemsCopy.sort((a, b) => a.price - b.price);
            case 'position':
            default:
                return itemsCopy.sort((a, b) => a.position - b.position);
        }
    }

    getBasketRequests(items: GroupedItem[] | undefined): Nexus.Lib.Basket.AddToBasketData[] {
        const requests = items
            ?.filter(item => item.selected)
            .map(item => ({
                id: item.id,
                qty: item.qty
            }));
        return requests || [];
    }

    validate(
        items: GroupedItem[] | undefined,
        config?: NProductConfiguration.Grouped.Config
    ): NProductConfiguration.Error.Entry[] | undefined {
        const errors: NProductConfiguration.Error.Entry[] = [];

        const selectedCount = items?.filter(item => item.selected).length || 0;
        const minSelection = config?.minSelection ?? 0;

        if (minSelection > 0 && selectedCount < minSelection) {
            errors.push({
                code: NProductConfiguration.Error.Code.noSelection,
                level: NProductConfiguration.Error.Level.error
            });
        }

        items
            ?.filter(item => item.selected)
            .forEach(item => {
                if (!item.isQtySalable) {
                    errors.push({
                        code: NProductConfiguration.Error.Code.outOfStock,
                        level: NProductConfiguration.Error.Level.error
                    });
                }
            });

        return errors.length > 0 ? errors : undefined;
    }
}
